import React, { useState, useEffect } from 'react';
import { PurpleNavBar } from '../components/PurpleNavBar';
import { COLORS } from '../../../core/constants/colors';
import { billingService } from '../../dashboard_user/services/billing.service';
import {
  SubscriptionPlan,
  ChargeRequest,
  ChargeResponse,
  SubscriptionStatus,
  Transaction,
} from '../../dashboard_user/types/billing.types';
import { PaymentModal } from '../../dashboard_user/components/billing/PaymentModal';
import { OTPModal } from '../../dashboard_user/components/billing/OTPModal';
import { useSubscription } from '../../../app/providers/SubscriptionProvider';

export const PartnerBillingPage: React.FC = () => {
  const { refreshSubscription } = useSubscription();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
  const [currentTransactionRef, setCurrentTransactionRef] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isValidatingOTP, setIsValidatingOTP] = useState(false);
  const [error, setError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [activeTab, setActiveTab] = useState<'plans' | 'history'>('plans');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [plansData, statusData, transactionsData] = await Promise.all([
        billingService.getPlans(),
        billingService.getSubscriptionStatus().catch(() => null),
        billingService.getTransactions(10).catch(() => ({ transactions: [], total: 0 })),
      ]);

      setPlans(plansData);
      setSubscriptionStatus(statusData);
      setTransactions(transactionsData.transactions || []);
    } catch (err: any) {
      console.error('Error loading billing data:', err);
      setError(err.message || 'Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setIsPaymentModalOpen(true);
    setError('');
  };

  const handleCharge = async (chargeData: ChargeRequest) => {
    setIsProcessingPayment(true);
    setError('');
    try {
      const response: ChargeResponse = await billingService.chargePayment(chargeData);

      if (response.status === 'OTP_REQUIRED') {
        setCurrentTransactionRef(response.transaction_ref);
        setIsPaymentModalOpen(false);
        setIsOTPModalOpen(true);
      } else if (response.status === 'SUCCESS') {
        setIsPaymentModalOpen(false);
        await loadData();
        alert('Payment completed successfully!');
      } else if (response.status === 'FAILED' || response.status === 'ERROR') {
        setError(response.message || 'Payment failed');
      } else {
        setError(response.message || 'Payment failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      setError(err.message || 'Failed to process payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleOTPValidate = async (otp: string, transactionRef: string) => {
    setIsValidatingOTP(true);
    setOtpError('');
    try {
      const response = await billingService.validateOTP({
        otp_code: otp,
        transaction_ref: transactionRef,
      });

      if (response.status === 'SUCCESS') {
        setIsOTPModalOpen(false);
        await loadData();
        await refreshSubscription();
        alert(response.message || 'Payment completed successfully!');
      } else {
        setOtpError(response.message || 'OTP validation failed');
      }
    } catch (err: any) {
      console.error('OTP validation error:', err);
      setOtpError(err.message || 'Failed to validate OTP');
    } finally {
      setIsValidatingOTP(false);
    }
  };

  const formatAmount = (amount: string, currency: string) => {
    try {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return amount;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numAmount);
    } catch {
      return `${currency} ${amount}`;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const activePlans = plans.filter((plan) => plan.is_active);

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ fontFamily: 'DM Sans, sans-serif' }}>
        <PurpleNavBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading billing information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <PurpleNavBar />
      <div className="min-h-screen bg-white">
        {/* Blue Banner Header Section */}
        <div style={{ backgroundColor: COLORS.user.primary }} className="relative overflow-hidden">
          {/* Background Pattern */}
          <div
            className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none"
            style={{ opacity: 0.15 }}
          >
            <img
              src="/new_logo_white_P.png"
              alt="Portfolio Indexing System"
              className="h-80 opacity-50"
            />
          </div>

          <div className="max-w-7xl mx-auto px-8 py-8 relative z-10">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-white mb-6" style={{ opacity: 0.9 }}>
              <span className="font-medium">Billing & Subscription</span>
            </div>

            {/* Header Content */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">
                  Billing & Subscription
                </h1>
                <p className="text-white text-sm" style={{ opacity: 0.9 }}>
                  Manage your subscription plans and payment history
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Subscription Status Card */}
          {subscriptionStatus && (
            <div className="bg-white rounded-lg border p-6 mb-6" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold mb-1 text-gray-900">Current Subscription</h3>
                  <p className="text-sm text-gray-600 capitalize mb-3">{subscriptionStatus.plan_type}</p>
                  <div className="flex items-center gap-6 text-sm">
                    <div>
                      <span className="text-gray-500">Status: </span>
                      <span
                        className={`font-medium ${subscriptionStatus.is_active ? 'text-green-600' : 'text-gray-500'}`}
                      >
                        {subscriptionStatus.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">End Date: </span>
                      <span className="font-medium text-gray-900">
                        {formatDate(subscriptionStatus.end_date || subscriptionStatus.start_date)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Days Remaining: </span>
                      <span className={`font-medium ${subscriptionStatus.days_remaining && subscriptionStatus.days_remaining <= 7 ? 'text-orange-600' : 'text-gray-900'}`}>
                        {subscriptionStatus.days_remaining !== null && subscriptionStatus.days_remaining !== undefined 
                          ? subscriptionStatus.days_remaining 
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div
                  className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    subscriptionStatus.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {subscriptionStatus.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b mb-6" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex gap-6">
              <button
                onClick={() => setActiveTab('plans')}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  activeTab === 'plans'
                    ? 'text-blue-600 border-b-2'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'plans' ? { borderBottomColor: COLORS.user.primary } : {}}
              >
                Subscription Plans
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`pb-3 px-1 text-sm font-medium transition-colors ${
                  activeTab === 'history'
                    ? 'text-blue-600 border-b-2'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={activeTab === 'history' ? { borderBottomColor: COLORS.user.primary } : {}}
              >
                Payment History
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'plans' && (
            <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E7EB' }}>
              <h2 className="text-lg font-bold text-gray-900 mb-6">Available Plans</h2>
              {activePlans.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm text-gray-500">No subscription plans available at the moment.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activePlans
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((plan) => (
                      <div
                        key={plan.id}
                        className="rounded-lg border p-6 hover:shadow-md transition-shadow cursor-pointer"
                        style={{ borderColor: '#E5E7EB' }}
                        onClick={() => handlePlanSelect(plan)}
                      >
                        {plan.is_featured && (
                          <div className="inline-block px-3 py-1 mb-4 rounded-full text-xs font-semibold text-white"
                            style={{ backgroundColor: COLORS.extended.orange }}
                          >
                            Featured
                          </div>
                        )}
                        <h3 className="text-xl font-bold mb-2 text-gray-900">{plan.name}</h3>
                        <div className="mb-4">
                          <span className="text-3xl font-bold" style={{ color: COLORS.user.primary }}>
                            {formatAmount(plan.amount, plan.currency)}
                          </span>
                          <span className="text-gray-600 ml-2">
                            /{plan.duration_days >= 30 ? `${Math.floor(plan.duration_days / 30)} mo` : `${plan.duration_days} days`}
                          </span>
                        </div>
                        {plan.description && (
                          <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                        )}
                        {plan.features && plan.features.length > 0 && (
                          <ul className="space-y-2 mb-6">
                            {plan.features.map((feature, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                                <svg
                                  className="w-5 h-5 flex-shrink-0 mt-0.5"
                                  style={{ color: COLORS.user.primary }}
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <button
                          className="w-full py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90"
                          style={{ backgroundColor: COLORS.user.primary }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlanSelect(plan);
                          }}
                        >
                          Select Plan
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="bg-white rounded-lg border" style={{ borderColor: '#E5E7EB' }}>
              <div className="p-6 border-b" style={{ borderColor: '#E5E7EB' }}>
                <h2 className="text-lg font-bold text-gray-900">Payment History</h2>
              </div>
              {transactions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: COLORS.user.primaryLight }}>
                    <svg
                      className="w-8 h-8"
                      style={{ color: COLORS.user.primary }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Transactions</h3>
                  <p className="text-sm text-gray-600">Your payment history will appear here</p>
                </div>
              ) : (
                <div className="divide-y" style={{ borderColor: '#E5E7EB' }}>
                  {transactions.map((transaction) => (
                    <div key={transaction.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-gray-900 capitalize">{transaction.plan_type}</h4>
                            <span
                              className="px-2 py-1 rounded-full text-xs font-semibold text-white"
                              style={{
                                backgroundColor:
                                  transaction.status.toLowerCase() === 'success' || transaction.status.toLowerCase() === 'completed'
                                    ? '#10b981'
                                    : transaction.status.toLowerCase() === 'failed' || transaction.status.toLowerCase() === 'error'
                                    ? COLORS.extended.redSalsa
                                    : COLORS.extended.orange,
                              }}
                            >
                              {transaction.status}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p>
                              <span className="font-medium">Ref:</span>{' '}
                              <span className="font-mono text-xs">{transaction.external_ref || transaction.baseone_ref}</span>
                            </p>
                            {transaction.baseone_ref && (
                              <p>
                                <span className="font-medium">Baseone Ref:</span>{' '}
                                <span className="font-mono text-xs">{transaction.baseone_ref}</span>
                              </p>
                            )}
                            <p className="text-xs text-gray-500">{formatDate(transaction.created_at)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold mb-1" style={{ color: COLORS.user.primary }}>
                            {formatAmount(transaction.amount, transaction.currency)}
                          </p>
                        </div>
                      </div>
                      {transaction.error_message && (
                        <div className="mt-3 p-2 rounded bg-red-50 border border-red-200">
                          <p className="text-xs text-red-600">{transaction.error_message}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedPlan(null);
          setError('');
        }}
        plan={selectedPlan}
        onCharge={handleCharge}
        isLoading={isProcessingPayment}
      />

      {/* OTP Modal */}
      <OTPModal
        isOpen={isOTPModalOpen}
        onClose={() => {
          setIsOTPModalOpen(false);
          setCurrentTransactionRef('');
          setOtpError('');
        }}
        onValidate={handleOTPValidate}
        transactionRef={currentTransactionRef}
        isLoading={isValidatingOTP}
        error={otpError}
      />
    </div>
  );
};
